import type { IOption } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  ExpenseBudgetItemType,
  type GetModalTransferCreditDependenciesQuery,
  type GetProjectBudgetItemsQuery,
} from "@src/__generated__/graphql";
import { currency } from "@src/utils/formatters";
import { filter, groupBy, map } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { FormTransferCredit } from "./forms/FormTransferCredit";

type TBrandCreditAccount = NonNullable<
  GetModalTransferCreditDependenciesQuery["brandCreditAccount"]
>;
type TProject = NonNullable<GetProjectBudgetItemsQuery["project"]>;

export class Store {
  @observable.ref brandCreditAccount?: TBrandCreditAccount;
  @observable.ref project?: TProject;
  @observable.ref form: FormTransferCredit;

  typeOptions: IOption[] = [
    {
      label: t`Transfer to project`,
      value: "project",
    },
    {
      label: t`Transfer to credit account`,
      value: "account",
    },
  ];

  requestToConfirmTransfer: boolean;
  requestConfirmed: boolean;

  constructor() {
    makeObservable(this);
    this.form = new FormTransferCredit(this);
    this.requestToConfirmTransfer = false;
    this.requestConfirmed = false;
  }

  @action
  setBrandCreditAccount(src: TBrandCreditAccount) {
    this.brandCreditAccount = src;
  }

  @action
  setProject(src: TProject) {
    this.project = src;
  }

  @computed get creditAccountOptions() {
    if (!this.brandCreditAccount?.brand) return [];

    return filter(
      map(this.brandCreditAccount.brand.client.brands, (brand) => ({
        label: brand.name,
        options: (brand.creditAccounts || [])
          .filter((i) => i.id !== this.brandCreditAccount?.id)
          .map((i) => ({ label: i.title, value: i.creditAccount.id })),
      })),
      (brand) => Boolean(brand.options.length),
    );
  }

  @computed get projectOptions() {
    if (!this.brandCreditAccount?.brand) return [];

    return filter(
      map(this.brandCreditAccount.brand.client.brands, (brand) => ({
        label: brand.name,
        options: map(
          filter(brand.projects, (p) => p.billable && !p.finished_at),
          (project) => ({
            label: `${project.code} – ${project.title}`,
            value: project.id,
          }),
        ),
      })),
      (brand) => Boolean(brand.options.length),
    );
  }

  @computed get budgetItemOptions() {
    if (!this.project) return [];

    const budgetItemsGroupedByCategory = groupBy(
      [...this.project.expenses, ...this.project.ourWork],
      (bi) => bi.category.name,
    );

    return filter(
      map(Object.keys(budgetItemsGroupedByCategory), (key) => ({
        label: key,
        options: map(
          filter(
            budgetItemsGroupedByCategory[key],
            (bi) => !("type" in bi && bi.type === ExpenseBudgetItemType.Other),
          ),
          (bi) => ({
            label: `${bi.title} (${currency.formatByWorkspace(
              bi.stats.credit,
            )}) - ${t`uninvoiced:`} ${currency.formatByWorkspace(bi.stats.uninvoiced)}`,
            value: bi.creditAccount!.id,
          }),
        ),
      })),
      (c) => Boolean(c.options.length),
    );
  }
}
